import { createTerminal } from './ui/terminal';
import { runBootSequence } from './ui/screens/boot-sequence';
import { runLoginScreen } from './ui/screens/login-screen';
import { runGameSelect } from './ui/screens/game-select';
import { runSideSelect } from './ui/screens/side-select';
import { runBriefing } from './ui/screens/briefing-screen';
import { runAllocation } from './ui/screens/allocation-screen';
import { runTargeting } from './ui/screens/targeting-screen';
import { runLaunch } from './ui/screens/launch-screen';
import { runAssessment } from './ui/screens/assessment-screen';
import { runEndgame } from './ui/screens/endgame-screen';
import { createInitialGameState, transition } from './engine/state-machine';
import { computeAIDecision } from './engine/ai-opponent';
import { executeStrike } from './engine/damage-model';
import { computeScore } from './engine/scoring';
import { checkGameOver } from './engine/game-loop';
import { createAudioSystem } from './ui/audio';
import type { MapConfig } from './ui/components/world-map';

async function main(): Promise<void> {
  const outputEl = document.getElementById('terminal-output');
  const containerEl = document.getElementById('terminal');
  const canvasEl = document.getElementById('war-map') as HTMLCanvasElement | null;

  if (!outputEl || !containerEl) throw new Error('Terminal elements not found');

  const terminal = createTerminal({
    outputElement: outputEl,
    containerElement: containerEl,
  });

  const audio = createAudioSystem();

  const mapConfig: MapConfig | null = canvasEl ? {
    canvas: canvasEl,
    primaryColor: '#33ff33',
    dimColor: 'rgba(51, 255, 51, 0.25)',
    dangerColor: '#ff3333',
    warningColor: '#ffcc33',
  } : null;

  // Screen turn-on effect
  const screen = document.getElementById('crt-screen');
  if (screen) {
    screen.classList.add('screen-on');
  }

  await terminal.pause(800);

  // Phase: Boot
  await runBootSequence(terminal);

  // Phase: Login
  await runLoginScreen(terminal);

  // Phase: Game Select
  const selectedGame = await runGameSelect(terminal);
  if (selectedGame === 'chess') {
    await terminal.println('\nA WISE CHOICE.\n', { cssClass: 'bright' });
    return;
  }

  // Phase: Side Select
  const playerSide = await runSideSelect(terminal);

  // Initialize game state
  let state = createInitialGameState(playerSide);

  // Phase: Briefing
  state = transition(state, { type: 'BRIEFING_ACKNOWLEDGED' });
  await runBriefing(terminal, state);

  // Game loop
  while (!checkGameOver(state)) {
    // Allocation
    state = transition(state, { type: 'START_ALLOCATION' });
    const allocation = await runAllocation(terminal, state);
    state = transition(state, { type: 'ALLOCATION_CONFIRMED', allocation });

    // Targeting
    state = transition(state, { type: 'START_TARGETING' });
    const strike = await runTargeting(terminal, state);
    state = transition(state, { type: 'TARGETS_CONFIRMED', strike });

    // Launch
    state = transition(state, { type: 'START_LAUNCH' });
    const playerResults = executeStrike(strike, state);
    state = transition(state, { type: 'LAUNCH_COMPLETE', results: playerResults });
    await runLaunch(terminal, state, strike, playerResults, mapConfig, audio);

    // Counterstrike
    state = transition(state, { type: 'START_COUNTERSTRIKE' });
    const aiDecision = computeAIDecision(state);
    if (aiDecision.strike) {
      const aiResults = executeStrike(aiDecision.strike, state);
      state = transition(state, { type: 'COUNTERSTRIKE_COMPLETE', results: aiResults, aiStrike: aiDecision.strike });
      await runAssessment(terminal, state, playerResults, aiResults, aiDecision);
    } else {
      state = transition(state, { type: 'COUNTERSTRIKE_COMPLETE', results: [], aiStrike: null });
      await runAssessment(terminal, state, playerResults, [], aiDecision);
    }

    // Check for continue
    if (!checkGameOver(state)) {
      await terminal.println('\n');
      await terminal.println('CONTINUE ENGAGEMENT? (Y/N)', { cssClass: 'warning' });
      const answer = await terminal.waitForInput('> ');
      if (answer.toUpperCase().startsWith('N')) {
        break;
      }
      state = transition(state, { type: 'NEXT_ROUND' });
    }
  }

  // Endgame
  const score = computeScore(state);
  state = transition(state, { type: 'END_GAME', result: score });
  await runEndgame(terminal, state, score);
}

main().catch(console.error);
